<template>
    <div class="request-callback">
        <form class="request-callback__form">
            <template v-if="isSuccess">
                <div class="request-callback__success-icon">
                    <CheckStatus class-name="icon-stroke icon_color_blue" />
                </div>
                <p class="request-callback__title">
                    Заявка принята
                </p>
                <p class="request-callback__description">
                    Ваше обращение успешно отправлено и мы<br>вернемся к вам с ответом в ближайшее время
                </p>
                <div class="request-callback__form-action">
                    <STBaseButton text="Закрыть"
                                  modify="button_theme_white"
                                  @click="hide" />
                </div>
            </template>
            <template v-else-if="isError">
                <div class="request-callback__alert-icon">
                    <IconAlert />
                </div>
                <p class="request-callback__title">
                    Ой, что-то пошло не так
                </p>
                <p class="request-callback__description">
                    Произошла непредвиденная ошибка<br>Пожалуйста попробуйте позже.
                </p>
                <div class="request-callback__form-action">
                    <STBaseButton text="Хорошо"
                                  @click="hide" />
                </div>
            </template>
            <template v-else>
                <p class="request-callback__title request-callback__title_theme_top-level">
                    Обратная связь
                </p>
                <p class="request-callback__description">
                    Партнерский отдел свяжется с&nbsp;вами в&nbsp;рабочее время: с&nbsp;понедельника по&nbsp;пятницу с&nbsp;10:00 до&nbsp;19:00
                </p>

                <div class="request-callback__inputs">
                    <STInput
                        id="request-callback-user-name"
                        v-model.trim="userName"
                        name="request-callback-user-name"
                        label="Ваше имя"
                        :error="nameErrorText"
                        :modify="{ 'is-error': $v.userName.$dirty && $v.userName.$invalid }"
                        @input="$v.userName.$touch()"
                        @focus="$v.userName.$touch()" />

                    <STInput
                        id="request-callback-user-phone"
                        ref="userPhoneInput"
                        :value="userPhone"
                        name="request-callback-user-phone"
                        label="Ваш номер телефона"
                        :error="phoneErrorText"
                        :modify="{ 'is-error': $v.userPhone.$dirty && $v.userPhone.$invalid }"
                        @input="onPhoneInput"
                        @focus="onPhoneFocus" />
                </div>

                <div :class="['request-callback__form-submit', {'is-loading': isLoading}]">
                    <STBaseButton text="Отправить"
                                  icon-load="IconLoad"
                                  modify="button_full_width"
                                  :is-loading="isLoading"
                                  :disabled="!isFieldsFilled"
                                  @click="requestCallback" />
                </div>
            </template>
        </form>
    </div>
</template>

<script>
import IconAlert from '@/components/Icons/Alert';
import CheckStatus from '@/components/Icons/CheckStatus';

import {mapActions, mapGetters} from 'vuex';
import {formatPhone, handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';

const onlyLetters = value => {
    if (!value) {
        return false;
    }

    if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(value)) {
        return false;
    }

    if (value.startsWith('-') || value.endsWith('-')) {
        return false;
    }

    return (value.match(/-/g) || []).length <= 3;
};

export default {
    name: 'RequestCallback',

    components: {
        IconAlert,
        CheckStatus
    },

    data() {
        return {
            isSuccess: false,
            isError  : false,
            isLoading: false,
            userName : '',
            userPhone: ''
        };
    },

    validations: {
        userName: {
            required,
            onlyLetters,
            minLength: minLength(3),
            maxLength: maxLength(40)
        },
        userPhone: {
            required,
            phoneLength: isPhoneComplete
        }
    },

    computed: {
        ...mapGetters({
            userDataLoaded: 'GET_IS_USER_DATA_LOADED',
            userData      : 'GET_USER_DATA',
        }),

        isFieldsFilled() {
            return this.userName && isPhoneComplete(this.userPhone);
        },

        nameErrorText() {
            return 'Это обязательное поле';
        },

        phoneErrorText() {
            return 'Это обязательное поле';
        },
    },

    mounted() {
        if (this.userDataLoaded) {
            this.applyUserData(this.userData);
        }
    },

    methods: {
        ...mapActions(['sendFeedback']),

        applyUserData(data) {
            this.userName = `${data.firstName || ''} ${data.surname || ''}`.trim();
            this.userPhone = formatPhone(data.phone);
        },

        /**
         * Отправляет запрос на обратную связь
         */
        async requestCallback() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isError = false;
            this.isSuccess = false;
            this.isLoading = true;

            const payload = {
                name : this.userName,
                phone: this.userPhone,
            };

            try {
                const { data } = await this.sendFeedback(payload);

                if (+data.status === 0 || data.error) {
                    this.handleRequestError(data.error);

                    return;
                }

                this._sendToComagic();

                this.isLoading = false;
                this.isError = false;
                this.isSuccess = true;
            } catch(error) {
                this.handleRequestError(error);
            }
        },

        /**
         * Отправляет данные в Comagic
         */
        _sendToComagic() {
            if (typeof window.Comagic !== 'undefined' && typeof window.Comagic.addOfflineRequest !== 'undefined') {
                window.Comagic.addOfflineRequest({
                    name     : this.userName,
                    phone    : this.userPhone,
                    form_name: 'Обратная связь'
                });
            }
        },

        /**
         * Закрыть попап
         */
        hide() {
            this.$v.$reset();
            this.$emit('close');
        },

        /**
         * Обработка ошибки отправки формы
         */
        handleRequestError(data) {
            this.isLoading = false;
            this.isSuccess = false;
            this.isError = true;

            console.error('Ошибка отправки формы:', JSON.stringify(data));
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'userPhone', 'userPhoneInput');
            this.$v.userPhone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'userPhone', 'userPhoneInput', value);
            this.$v.userPhone.$touch();
        },
    }
};
</script>

<style lang="scss">
@import "RequestCallback";
</style>
